import createTheme from '@material-ui/core/styles/createTheme';
import createPalette from '@material-ui/core/styles/createPalette';

import shadows from '@material-ui/core/styles/shadows';
import { COLOURS, ICON_SIZE, BUTTON_MARGIN_RIGHT, BREAKPOINTS } from './consts';
import { IPalette } from './theme';

const palette = createPalette({
  text: {
    primary: COLOURS.WHITE,
    secondary: COLOURS.TEXT,
  },
  primary: {
    main: COLOURS.MENUBAR_GREY,
  },
  secondary: {
    main: COLOURS.LIGHT_GREY,
  },
  error: {
    main: COLOURS.RED,
  },

  type: 'light',
  // @ts-ignore
  surfaceColor: {
    primary: COLOURS.SURFACE_PRIMARY,
    secondary: COLOURS.SURFACE_SECONDARY,
    background: COLOURS.WHITE,
    hover: COLOURS.SURFACE_HOVER,
  },
  onSurfaceColor: {
    primary: COLOURS.EXTRA_DARK,
    secondary: COLOURS.MENUBAR_GREY,
    tertiary: COLOURS.LIGHT_GREY,
    selected: COLOURS.ON_SURFACE_SELECTED,
  },
  context: {
    error_primary: COLOURS.RED,
    error_secondary: COLOURS.TABLE_RED_LIGHT,
    variants_1: COLOURS.OWNER,
    variants_2: COLOURS.GREEN,
    variants_3: COLOURS.RED,
    variants_4: COLOURS.YELLOW,
    variants_5: '#334BA1',
    variants_6: '#8A8A8D',
  },
  inputColor: {
    secondary: COLOURS.SURFACE_SECONDARY,
  },
  buttonColor: {
    primary: COLOURS.OWNER,
    disabled: COLOURS.LINE_GREY,
  },
  onButtonColor: {
    secondary: COLOURS.OWNER,
    disabled: COLOURS.LIGHT_GREY,
  },
}) as IPalette;

const theme = createTheme({
  palette,
  shadows,
  breakpoints: {
    values: {
      xs: 0,
      sm: BREAKPOINTS.SM,
      md: BREAKPOINTS.MD,
      lg: BREAKPOINTS.LG,
      xl: BREAKPOINTS.XL,
    },
  },
  overrides: {
    MuiSvgIcon: {
      root: {
        fontSize: ICON_SIZE.MD,
        verticalAlign: 'middle', // для таблиц
      },
      fontSizeSmall: {
        fontSize: ICON_SIZE.SM,
      },
      fontSizeLarge: {
        fontSize: ICON_SIZE.LG,
      },
    },
    MuiDialog: {
      paperWidthXs: {
        maxWidth: BREAKPOINTS.XS,
      },
    },
    MuiDialogContent: {
      root: {
        width: '100%',
      },
    },
    MuiDialogActions: {
      root: {
        width: '100%',
        margin: '20px 0px 0px',
        textAlign: 'right',
        flexWrap: 'wrap',
        '&>*': {
          margin: '0 !important',
          marginLeft: `${BUTTON_MARGIN_RIGHT}px !important`,
          '&:first-child': {
            marginLeft: '0 !important',
          },
        },
      },
    },
    MuiDrawer: {
      paperAnchorDockedLeft: {
        borderRight: 0,
      },
    },
    MuiPaper: {
      root: {
        color: COLOURS.TEXT,
        backgroundColor: COLOURS.LINE_GREY,
      },
      elevation2: {
        boxShadow: 'none',
        borderRadius: 5,
      },
      elevation8: {
        boxShadow: '0 2px 15px 0 rgba(0, 0, 0, 0.12)',
      },
      elevation4: {
        boxShadow: '0 0 2px 0 rgba(0, 0, 0, 0.3)',
      },
    },
    MuiTab: {
      root: {
        fontFamily: 'IBM Plex Sans',
        textTransform: 'capitalize',
      },
    },
    MuiTableCell: {
      root: {
        padding: '4px 10px',
        borderBottom: 0,
      },
      body: {
        height: 40, // min row height 40px
        fontSize: 12,
        border: 0,
        color: COLOURS.TEXT,
        fontWeight: 500,
        fontFamily: 'IBM Plex Sans',
      },
      head: {
        height: 32, // min header height 32px
        fontWeight: 500,
        fontSize: 11,
        lineHeight: 'inherit',
        borderRight: `1px solid ${COLOURS.LIGHT_GREY}`,
        verticalAlign: 'middle',
        backgroundColor: COLOURS.MENUBAR_GREY,
        color: COLOURS.WHITE,
        whiteSpace: 'nowrap',

        '&:last-child': {
          borderRight: 0,
        },
      },
      stickyHeader: {
        backgroundColor: COLOURS.MENUBAR_GREY,
        color: COLOURS.WHITE,
      },
    },
    MuiTable: {
      root: {
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
      },
    },
    MuiTableRow: {
      root: {
        '&:hover': {
          backgroundColor: COLOURS.LINE_GREY,
        },
      },
      head: {
        border: 0,
      },
    },
    MuiTypography: {
      h3: {
        fontSize: 24,
        fontWeight: 'bold',
      },
      h4: {
        fontSize: 20,
        fontWeight: 'bold',
      },
      h5: {
        fontSize: 17,
        lineHeight: '21px',
        color: palette.text.secondary,
        fontWeight: 'normal',
        opacity: 0.8,
      },
      h6: {
        fontSize: 17,
        fontWeight: 700,
        lineHeight: '20px',
      },
      body1: {
        fontSize: 12,
        color: palette.text.primary,
        fontWeight: 600,
      },
      h1: {
        fontSize: 24,
        color: palette.text.primary,
        fontWeight: 500,
        lineHeight: '32px',
        letterSpacing: 'normal',
      },
      subtitle1: {
        fontSize: 12,
        fontWeight: 'normal',
        lineHeight: 1.6, // как в body
        color: palette.text.secondary,
      },
    },
    MuiExpansionPanel: {
      root: {
        marginBottom: 10,
        boxShadow: 'none',
        '&:before': {
          content: 'none',
        },
        '&:last-child': {
          marginBottom: 0,
        },
        '&$expanded': {
          margin: '0 0 10px',
          '&:last-child': {
            marginBottom: 0,
          },
        },
      },
    },
    MuiExpansionPanelSummary: {
      root: {
        fontWeight: 700,
        color: palette.text.primary,
        '&$expanded': {
          minHeight: 0,
        },
      },
      content: {
        '&$expanded': {
          margin: '15px 0 !important',
        },
      },
    },
    MuiSnackbar: {
      root: {
        padding: '0px 10px 10px',
        margin: '10px 0px 0px',
      },
      anchorOriginTopRight: {
        top: '60px !important',
        right: '0 !important',
        left: 'auto',
      },
      anchorOriginTopLeft: {
        top: '60px !important',
        left: '240px !important',
      },
      anchorOriginBottomLeft: {
        left: '240px !important',
        bottom: '0 !important',
      },
      anchorOriginTopCenter: {
        top: '60px !important',
        left: '0',
        maxWidth: '100vw',
        width: '100vw',
        padding: '0',
      },
    },
    MuiSnackbarContent: {
      root: {
        width: 400,
        position: 'relative',
        padding: 0,
        boxShadow: 'rgba(0, 0, 0, 0.07) 0px 2px 8px 0px',
        borderRadius: 0,
      },
      message: {
        padding: 20,
      },
      action: {
        cursor: 'pointer',
        fontSize: 17,
        position: 'absolute',
        width: 14,
        height: 14,
        top: 20,
        right: 26,
        padding: '0 !important',
      },
    },
  },
});

export { palette, theme };
